/**
 * Wishlist — localStorage-based, no app required.
 */

const STORAGE_KEY = 'lookdyno_wishlist';

export const Wishlist = window.Wishlist = {
  getAll() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  },
  save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  },
  has(id) { return this.getAll().some(i => i.id === id); },
  add(product) {
    const items = this.getAll();
    if (!this.has(product.id)) { items.push(product); this.save(items); }
  },
  remove(id) { this.save(this.getAll().filter(i => i.id !== id)); },
  toggle(product) {
    if (this.has(product.id)) { this.remove(product.id); return false; }
    else { this.add(product); return true; }
  },
  count() { return this.getAll().length; },
};

// ─── Toast ───────────────────────────────────────────────────

let toastTimeout;

function showToast(title, subtitle, type = 'add') {
  let el = document.getElementById('wl-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'wl-toast';
    document.body.appendChild(el);
  }

  clearTimeout(toastTimeout);
  el.className = `wl-toast wl-toast--${type}`;

  el.innerHTML = `
    <div class="wl-toast__icon">
      ${type === 'add'
        ? `<svg viewBox="0 0 24 24" fill="none" width="18" height="18"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor"/></svg>`
        : `<svg viewBox="0 0 24 24" fill="none" width="18" height="18"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" stroke-width="1.8"/></svg>`
      }
    </div>
    <div class="wl-toast__body">
      <span class="wl-toast__title">${title}</span>
      <span class="wl-toast__sub">${subtitle}</span>
    </div>
    <button class="wl-toast__close" aria-label="Close" onclick="this.closest('#wl-toast').classList.remove('wl-toast--show')">
      <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
    </button>
    <div class="wl-toast__bar"></div>
  `;

  // Force reflow then show
  el.classList.remove('wl-toast--show');
  requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('wl-toast--show')));

  toastTimeout = setTimeout(() => el.classList.remove('wl-toast--show'), 3200);
}

// ─── Toast styles ─────────────────────────────────────────────

if (!document.getElementById('wl-toast-styles')) {
  const s = document.createElement('style');
  s.id = 'wl-toast-styles';
  s.textContent = `
    #wl-toast {
      position: fixed;
      bottom: 28px;
      right: 24px;
      z-index: 99999;
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 240px;
      max-width: 320px;
      padding: 14px 14px 14px 16px;
      border-radius: 14px;
      background: #1a1a1a;
      color: #fff;
      box-shadow: 0 8px 32px rgb(0 0 0 / 0.22), 0 2px 8px rgb(0 0 0 / 0.12);
      opacity: 0;
      transform: translateY(16px) scale(0.96);
      transition: opacity 0.3s cubic-bezier(0.34,1.56,0.64,1),
                  transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
      pointer-events: none;
      overflow: hidden;
    }

    #wl-toast.wl-toast--show {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: auto;
    }

    @media (max-width: 480px) {
      #wl-toast {
        left: 16px;
        right: 16px;
        bottom: 20px;
        max-width: none;
        min-width: 0;
      }
    }

    .wl-toast__icon {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .wl-toast--add .wl-toast__icon {
      background: rgb(229 57 53 / 0.15);
      color: #ff6b6b;
    }

    .wl-toast--remove .wl-toast__icon {
      background: rgb(255 255 255 / 0.08);
      color: #aaa;
    }

    .wl-toast__body {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .wl-toast__title {
      font-size: 13px;
      font-weight: 600;
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .wl-toast__sub {
      font-size: 11px;
      opacity: 0.5;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .wl-toast__close {
      flex-shrink: 0;
      background: none;
      border: none;
      color: rgb(255 255 255 / 0.4);
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: color 0.15s, background 0.15s;
    }

    .wl-toast__close:hover {
      color: #fff;
      background: rgb(255 255 255 / 0.1);
    }

    /* Progress bar */
    .wl-toast__bar {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 100%;
      background: rgb(255 255 255 / 0.15);
      transform-origin: left;
    }

    #wl-toast.wl-toast--show .wl-toast__bar {
      animation: wl-bar 3.2s linear forwards;
    }

    @keyframes wl-bar {
      from { transform: scaleX(1); }
      to   { transform: scaleX(0); }
    }

    .wl-toast--add .wl-toast__bar { background: rgb(229 57 53 / 0.5); }
  `;
  document.head.appendChild(s);
}

// ─── WishlistButton ───────────────────────────────────────────

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.#render();
    this.#sync();
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('wishlist:updated', this.#sync);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('wishlist:updated', this.#sync);
  }

  #render() {
    this.innerHTML = `
      <button class="wishlist-btn" type="button" aria-label="Add to wishlist" aria-pressed="false">
        <svg class="wishlist-btn__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path class="wishlist-btn__heart" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
        <span class="wishlist-btn__label"></span>
        <span class="wishlist-btn__ripple"></span>
      </button>`;
  }

  #sync = () => {
    const btn = this.querySelector('button');
    if (!btn) return;
    const active = Wishlist.has(this.dataset.productId);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    this.toggleAttribute('active', active);
    const label = this.querySelector('.wishlist-btn__label');
    if (label && this.classList.contains('product-page-wishlist')) {
      label.textContent = active ? 'Saved' : 'Save';
    }
  };

  #handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const product = {
      id: this.dataset.productId,
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      image: this.dataset.productImage || '',
      price: this.dataset.productPrice || '',
    };

    const btn = this.querySelector('button');
    btn?.classList.add('wishlist-btn--animating');
    setTimeout(() => btn?.classList.remove('wishlist-btn--animating'), 600);

    const added = Wishlist.toggle(product);

    showToast(
      added ? 'Added to wishlist' : 'Removed from wishlist',
      product.title,
      added ? 'add' : 'remove'
    );
  };
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);

// ─── WishlistCount ────────────────────────────────────────────

class WishlistCount extends HTMLElement {
  connectedCallback() { this.#update(); window.addEventListener('wishlist:updated', this.#update); }
  disconnectedCallback() { window.removeEventListener('wishlist:updated', this.#update); }
  #update = () => {
    const count = Wishlist.count();
    const badge = this.querySelector('.wishlist-count__badge');
    if (badge) { badge.textContent = count > 0 ? String(count) : ''; badge.toggleAttribute('hidden', count === 0); }
    this.toggleAttribute('has-items', count > 0);
  };
}

if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);

// ─── Page renderer ────────────────────────────────────────────

export function renderWishlistPage(containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;

  const aspectRatio = container.dataset.aspectRatio || 'portrait';

  // ── ONLY CHANGE: expanded ratio map ──
  const ratioMap = {
    portrait:      '4 / 5',
    portrait_tall: '4 / 6',
    square:        '1 / 1',
    adapt:         'auto',
  };
  const ratio = ratioMap[aspectRatio] || '4 / 5';

  const render = () => {
    const items = Wishlist.getAll();

    if (items.length === 0) {
      container.innerHTML = `
        <div class="wl-empty">
          <div class="wl-empty__icon-wrap">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </div>
          <h2 class="wl-empty__title">Your wishlist is empty</h2>
          <p class="wl-empty__text">Browse our collection and save the products you love.</p>
          <a href="/collections/all" class="wl-empty__cta">Explore collection</a>
        </div>`;
      return;
    }

    container.innerHTML = `
      <ul class="wl-grid" style="--wl-ratio:${ratio}">
        ${items.map(item => `
          <li class="wl-card" data-id="${item.id}">
            <a href="${item.url}" class="wl-card__img-link">
              <div class="wl-card__img-wrap">
                ${item.image
                  ? `<img src="${item.image}" alt="${item.title}" class="wl-card__img" loading="lazy">`
                  : `<div class="wl-card__img-placeholder"></div>`}
              </div>
            </a>
            <button class="wl-card__remove" type="button" data-id="${item.id}" aria-label="Remove from wishlist">
              <svg viewBox="0 0 24 24" fill="none" width="14" height="14" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
            <div class="wl-card__body">
              <a href="${item.url}" class="wl-card__title">${item.title}</a>
              ${item.price ? `<p class="wl-card__price">${item.price}</p>` : ''}
              <button class="wl-card__atc" type="button" data-url="${item.url}" data-title="${item.title}">Add to cart</button>
            </div>
          </li>`).join('')}
      </ul>`;
// ── Add to Cart ──────────────────────────────────────────
    container.querySelectorAll('.wl-card__atc').forEach(btn => {
      btn.addEventListener('click', async () => {
        const productUrl = btn.dataset.url;
        const productTitle = btn.dataset.title;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const path = productUrl.replace(/^https?:\/\/[^/]+/, '').split('?')[0].replace(/\/$/, '');
          const res = await fetch(`${path}.js`);
          const product = await res.json();
          const variant = product.variants.find(v => v.available) || product.variants[0];
          if (!variant) throw new Error('No variant');
          const cartRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variant.id, quantity: 1 })
          });
          if (!cartRes.ok) throw new Error('Cart error');
          btn.textContent = '✓ Added';
          showToast('Added to cart', productTitle, 'add');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          fetch('/cart.js').then(r => r.json()).then(cart => {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          });
          setTimeout(() => { btn.textContent = 'Add to cart'; btn.disabled = false; }, 2000);
        } catch (e) {
          btn.textContent = 'Add to cart';
          btn.disabled = false;
          window.location.href = productUrl;
        }
      });
    });
    container.querySelectorAll('.wl-card__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.id;
        const card = container.querySelector(`.wl-card[data-id="${id}"]`);
        const title = card?.querySelector('.wl-card__title')?.textContent || '';
        if (card) {
          card.classList.add('wl-card--out');
          card.addEventListener('animationend', () => {
            Wishlist.remove(id);
            showToast('Removed from wishlist', title, 'remove');
          }, { once: true });
        }
      });
    });
  };

  render();
  window.addEventListener('wishlist:updated', render);
}
